/**
 * WI Header Engine — Skeleton Framework Production Script
 * Viewport lock, sticky isolation, search triggering, and zero scroll-jump guards.
 * Desktop: Permanently/consistently sticky.
 * Mobile: Directional scroll hide/reveal.
 */
(() => {
  const DESKTOP_QUERY = '(min-width: 990px)';
  const assetPromises = new Map();

  const loadStylesheet = (url) => {
    if (!url) return Promise.resolve();
    const key = `style:${url}`;
    if (assetPromises.has(key)) return assetPromises.get(key);

    const existing = [...document.styleSheets].some((sheet) => sheet.href === url);
    if (existing) {
      const resolved = Promise.resolve();
      assetPromises.set(key, resolved);
      return resolved;
    }

    const promise = new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      link.dataset.wiLazyAsset = 'search-style';
      link.addEventListener('load', resolve, { once: true });
      link.addEventListener('error', reject, { once: true });
      document.head.appendChild(link);
    });

    assetPromises.set(key, promise);
    return promise;
  };

  const loadScript = (url) => {
    if (!url) return Promise.resolve();
    const key = `script:${url}`;
    if (assetPromises.has(key)) return assetPromises.get(key);

    const existing = [...document.scripts].find((script) => script.src === url);
    if (existing) {
      const resolved = existing.dataset.wiLoaded === 'true'
        ? Promise.resolve()
        : new Promise((resolve, reject) => {
            existing.addEventListener('load', resolve, { once: true });
            existing.addEventListener('error', reject, { once: true });
          });
      assetPromises.set(key, resolved);
      return resolved;
    }

    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.dataset.wiLazyAsset = 'search-script';
      script.addEventListener('load', () => {
        script.dataset.wiLoaded = 'true';
        resolve();
      }, { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });

    assetPromises.set(key, promise);
    return promise;
  };

  const getSectionRoot = (header) => header.closest('.shopify-section') || header.parentElement;

  function initHeader(header) {
    if (!header || header.dataset.wiHeaderReady === 'true') return;
    header.dataset.wiHeaderReady = 'true';

    const controller = new AbortController();
    const { signal } = controller;
    const menuItems = [...header.querySelectorAll('[data-wi-menu-item]')];
    const mobileToggle = header.querySelector('[data-wi-mobile-toggle]');
    const navigation = header.querySelector('[data-wi-navigation]');
    const searchTriggers = [...header.querySelectorAll('[data-wi-search-open]')];
    const desktopMedia = window.matchMedia(DESKTOP_QUERY);
    const sectionRoot = getSectionRoot(header);
    const stickyEnabled = header.dataset.stickyHeader === 'true';
    const overlayEnabled = header.dataset.overlayHomeHero === 'true';
    const hero = overlayEnabled ? document.querySelector('[data-wi-hero-root]') : null;

    let closeTimer = 0;
    let searchAssetsPromise = null;
    let stickyRaf = 0;
    let headerResizeObserver = null;
    let lastScrollY = Math.max(0, window.scrollY);
    let headerHidden = false;
    let savedScrollPosition = 0;

    const syncHeaderSectionState = () => {
      if (!sectionRoot) return;

      sectionRoot.classList.toggle('wi-header-section--sticky', stickyEnabled);
      sectionRoot.classList.toggle('wi-header-section--overlay', overlayEnabled);

      const headerHeight = Math.max(1, Math.round(header.getBoundingClientRect().height));
      sectionRoot.style.setProperty('--wi-header-section-height', `${headerHeight}px`);
    };

    const setStickyVisibility = (hidden) => {
      if (!sectionRoot || !stickyEnabled) return;

      const isDesktop = desktopMedia.matches;
      const mobileMenuOpen = header.classList.contains('is-mobile-open');
      const headerHasFocus = header.contains(document.activeElement);

      // On desktop, header must remain consistently sticky (never hidden)
      const shouldHide = Boolean(!isDesktop && hidden && !mobileMenuOpen && !headerHasFocus);
      if (shouldHide === headerHidden) return;

      headerHidden = shouldHide;
      sectionRoot.classList.toggle('is-scroll-hidden', shouldHide);
    };

    const updateStickyDirection = () => {
      if (!stickyEnabled || header.classList.contains('is-mobile-open')) return;

      // On desktop, keep header locked in view and bypass scroll-hide calculations
      if (desktopMedia.matches) {
        setStickyVisibility(false);
        lastScrollY = Math.max(0, window.scrollY);
        return;
      }

      const currentY = Math.max(0, window.scrollY);
      const delta = currentY - lastScrollY;
      const headerHeight = Math.max(1, header.getBoundingClientRect().height);

      if (currentY <= Math.max(12, headerHeight * 0.35)) {
        setStickyVisibility(false);
        lastScrollY = currentY;
        return;
      }

      if (Math.abs(delta) < 6) return;

      if (delta > 0) {
        setStickyVisibility(true);
      } else {
        setStickyVisibility(false);
      }

      lastScrollY = currentY;
    };

    const updateOverlayScrollState = () => {
      stickyRaf = 0;

      if (!stickyEnabled || !overlayEnabled) {
        header.classList.remove('is-overlay-scrolled');
        return;
      }

      const heroBottom = hero ? hero.getBoundingClientRect().bottom : 0;
      const headerHeight = header.getBoundingClientRect().height;
      header.classList.toggle(
        'is-overlay-scrolled',
        !hero || heroBottom <= headerHeight
      );
    };

    const scheduleOverlayScrollState = () => {
      if (stickyRaf) return;
      stickyRaf = window.requestAnimationFrame(updateOverlayScrollState);
    };

    syncHeaderSectionState();

    if ('ResizeObserver' in window) {
      headerResizeObserver = new ResizeObserver(() => {
        syncHeaderSectionState();
        scheduleOverlayScrollState();
      });
      headerResizeObserver.observe(header);
    }

    if (stickyEnabled) {
      const handleStickyScroll = () => {
        updateStickyDirection();
        if (overlayEnabled) scheduleOverlayScrollState();
      };

      window.addEventListener('scroll', handleStickyScroll, {
        signal,
        passive: true
      });

      window.addEventListener('resize', () => {
        setStickyVisibility(false);
        lastScrollY = Math.max(0, window.scrollY);
        if (overlayEnabled) scheduleOverlayScrollState();
      }, {
        signal,
        passive: true
      });

      if (overlayEnabled) scheduleOverlayScrollState();
    }

    const closeItem = (item) => {
      if (!item) return;
      item.classList.remove('is-open');
      item.querySelector(':scope > .wi-header__nav-link-row [data-wi-menu-toggle]')
        ?.setAttribute('aria-expanded', 'false');
    };

    const closeAllItems = (except = null) => {
      menuItems.forEach((item) => {
        if (item !== except) closeItem(item);
      });
    };

    const openItem = (item) => {
      if (!item) return;
      const panel = item.querySelector(':scope > [data-wi-menu-panel]');
      const toggle = item.querySelector(':scope > .wi-header__nav-link-row [data-wi-menu-toggle]');
      if (!panel || !toggle) return;

      window.clearTimeout(closeTimer);
      closeAllItems(item);
      item.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
    };

    const scheduleDesktopClose = (item) => {
      if (!desktopMedia.matches) return;
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => closeItem(item), 110);
    };

    const setMobileMenu = (open, { restoreFocus = false } = {}) => {
      if (!mobileToggle || !navigation) return;

      const isCurrentlyOpen = header.classList.contains('is-mobile-open');
      const shouldOpen = !desktopMedia.matches && Boolean(open);

      if (isCurrentlyOpen === shouldOpen) return;

      header.classList.toggle('is-mobile-open', shouldOpen);
      sectionRoot?.classList.toggle('is-mobile-menu-active', shouldOpen);

      mobileToggle.setAttribute('aria-expanded', String(shouldOpen));
      mobileToggle.setAttribute(
        'aria-label',
        shouldOpen ? header.dataset.i18nCloseMenu : header.dataset.i18nOpenMenu
      );

      if (shouldOpen) {
        setStickyVisibility(false);
        savedScrollPosition = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${savedScrollPosition}px`;
        document.body.style.width = '100%';
        document.body.classList.add('wi-header-locked');
      } else {
        if (document.body.classList.contains('wi-header-locked')) {
          document.body.style.position = '';
          document.body.style.top = '';
          document.body.style.width = '';
          document.body.classList.remove('wi-header-locked');
          window.scrollTo(0, savedScrollPosition);
        }
        closeAllItems();
      }

      if (desktopMedia.matches) {
        navigation.removeAttribute('aria-hidden');
        navigation.inert = false;
      } else {
        navigation.setAttribute('aria-hidden', String(!shouldOpen));
        navigation.inert = !shouldOpen;
      }

      if (restoreFocus) mobileToggle.focus({ preventScroll: true });
    };

    menuItems.forEach((item) => {
      const toggle = item.querySelector(':scope > .wi-header__nav-link-row [data-wi-menu-toggle]');
      const panel = item.querySelector(':scope > [data-wi-menu-panel]');
      const link = item.querySelector(':scope > .wi-header__nav-link-row > .wi-header__nav-link');
      if (!toggle || !panel) return;

      item.addEventListener('mouseenter', () => {
        if (desktopMedia.matches) openItem(item);
      }, { signal });

      item.addEventListener('mouseleave', () => scheduleDesktopClose(item), { signal });

      item.addEventListener('focusin', () => {
        if (desktopMedia.matches) openItem(item);
      }, { signal });

      item.addEventListener('focusout', (event) => {
        if (desktopMedia.matches && !item.contains(event.relatedTarget)) closeItem(item);
      }, { signal });

      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const isOpen = item.classList.contains('is-open');
        if (isOpen) {
          closeItem(item);
        } else {
          openItem(item);
        }
      }, { signal });

      if (link && (link.getAttribute('href') === '#' || !link.getAttribute('href'))) {
        link.addEventListener('click', (event) => {
          if (!desktopMedia.matches) {
            event.preventDefault();
            const isOpen = item.classList.contains('is-open');
            if (isOpen) closeItem(item);
            else openItem(item);
          }
        }, { signal });
      }
    });

    mobileToggle?.addEventListener('click', () => {
      setStickyVisibility(false);
      setMobileMenu(!header.classList.contains('is-mobile-open'));
    }, { signal });

    navigation?.addEventListener('click', (event) => {
      if (desktopMedia.matches) return;
      const targetLink = event.target.closest('a');
      if (targetLink && targetLink.getAttribute('href') !== '#' && !targetLink.hasAttribute('data-wi-search-open')) {
        setMobileMenu(false);
      }
    }, { signal });

    const ensureSearchAssets = () => {
      if (!searchTriggers.length) return Promise.resolve();
      if (!searchAssetsPromise) {
        searchAssetsPromise = Promise.all([
          loadStylesheet(header.dataset.searchCssUrl),
          loadScript(header.dataset.searchJsUrl)
        ]);
      }
      return searchAssetsPromise;
    };

    const mountSearchModal = (trigger) => {
      if (!sectionRoot) return null;

      const modalId = header.dataset.searchModalId;
      const existing = modalId ? sectionRoot.querySelector(`#${CSS.escape(modalId)}`) : null;
      if (existing) return existing;

      const templateId = header.dataset.searchTemplateId;
      const mountId = header.dataset.searchMountId;
      const template = templateId ? sectionRoot.querySelector(`#${CSS.escape(templateId)}`) : null;
      const mount = mountId ? sectionRoot.querySelector(`#${CSS.escape(mountId)}`) : null;
      if (!(template instanceof HTMLTemplateElement) || !mount) return null;

      mount.appendChild(template.content.cloneNode(true));
      const modal = modalId ? mount.querySelector(`#${CSS.escape(modalId)}`) : mount.querySelector('[data-wi-search-modal]');

      if (modalId && trigger) trigger.setAttribute('aria-controls', modalId);
      return modal;
    };

    const openSearch = async (event, trigger) => {
      if (!trigger) return;
      event?.preventDefault();

      if (!desktopMedia.matches && header.classList.contains('is-mobile-open')) {
        setMobileMenu(false);
      }

      const modal = mountSearchModal(trigger);
      if (!modal) {
        window.location.assign(trigger.href);
        return;
      }

      try {
        await ensureSearchAssets();
        window.WISearch?.open(modal, trigger);
      } catch (error) {
        console.error('[WI Header] Search assets failed to load.', error);
        window.location.assign(trigger.href);
      }
    };

    searchTriggers.forEach((trigger) => {
      trigger.addEventListener('pointerenter', () => {
        ensureSearchAssets().catch(() => {});
      }, { signal, passive: true });
      trigger.addEventListener('focus', () => {
        ensureSearchAssets().catch(() => {});
      }, { signal });
      trigger.addEventListener('click', (e) => openSearch(e, trigger), { signal });
    });

    const handleOutsidePointer = (event) => {
      if (!header.contains(event.target)) {
        closeAllItems();
        if (!desktopMedia.matches && header.classList.contains('is-mobile-open')) {
          setMobileMenu(false);
        }
      }
    };

    document.addEventListener('pointerdown', handleOutsidePointer, { signal, passive: true });

    header.addEventListener('pointerenter', () => {
      setStickyVisibility(false);
    }, { signal, passive: true });

    header.addEventListener('focusin', () => {
      setStickyVisibility(false);
    }, { signal });

    header.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;

      if (!desktopMedia.matches && header.classList.contains('is-mobile-open')) {
        setMobileMenu(false, { restoreFocus: true });
        return;
      }

      const openToggle = header.querySelector('[data-wi-menu-toggle][aria-expanded="true"]');
      closeAllItems();
      openToggle?.focus({ preventScroll: true });
    }, { signal });

    const handleBreakpointChange = () => {
      window.clearTimeout(closeTimer);
      closeAllItems();
      if (desktopMedia.matches) {
        setStickyVisibility(false);
      } else if (header.classList.contains('is-mobile-open')) {
        setMobileMenu(false);
      }
    };

    desktopMedia.addEventListener?.('change', handleBreakpointChange);

    const destroy = () => {
      window.clearTimeout(closeTimer);
      if (stickyRaf) window.cancelAnimationFrame(stickyRaf);
      headerResizeObserver?.disconnect();

      controller.abort();
      desktopMedia.removeEventListener?.('change', handleBreakpointChange);

      if (document.body.classList.contains('wi-header-locked')) {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.classList.remove('wi-header-locked');
      }

      sectionRoot?.classList.remove(
        'wi-header-section--sticky',
        'wi-header-section--overlay',
        'is-scroll-hidden',
        'is-mobile-menu-active'
      );
      sectionRoot?.style.removeProperty('--wi-header-section-height');
      header.classList.remove('is-overlay-scrolled');

      const modalId = header.dataset.searchModalId;
      const modal = modalId && sectionRoot
        ? sectionRoot.querySelector(`#${CSS.escape(modalId)}`)
        : null;
      if (modal) window.WISearch?.destroy(modal);

      delete header.wiHeaderController;
      delete header.dataset.wiHeaderReady;
    };

    header.wiHeaderController = {
      openItem,
      setMobileMenu,
      destroy
    };
  }

  function initAllHeaders(scope = document) {
    scope.querySelectorAll('[data-wi-header]').forEach(initHeader);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAllHeaders(), { once: true });
  } else {
    initAllHeaders();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAllHeaders(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-wi-header]').forEach((header) => {
      header.wiHeaderController?.destroy();
    });
  });

  document.addEventListener('shopify:block:select', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const panel = target.matches('[data-wi-menu-panel]')
      ? target
      : target.querySelector('[data-wi-menu-panel]');
    if (!panel) return;

    const item = panel.closest('[data-wi-menu-item]');
    const header = item?.closest('[data-wi-header]');
    if (!item || !header) return;

    if (!window.matchMedia(DESKTOP_QUERY).matches) {
      header.wiHeaderController?.setMobileMenu(true);
    }
    header.wiHeaderController?.openItem(item);
  });
})();